import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  CircularProgress,
  Fade,
} from '@mui/material';
import { API_CONFIG } from '../config/config';
import logo from '../assets/logo_blau.png';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Bitte gib eine gültige E-Mail-Adresse ein')
    .required('E-Mail-Adresse ist erforderlich'),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError('');
      setSuccess(null);
      setLoading(true);

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/password-reset/request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 429) {
            // Handle rate limit error
            const retryAfter = response.headers.get('Retry-After');
            const waitTime = retryAfter ? parseInt(retryAfter) : 60;
            throw new Error(`Zu viele Anfragen. Bitte warte ${waitTime} Sekunden, bevor du es erneut versuchst.`);
          }
          throw new Error(data.detail || 'Fehler beim Anfordern des Passwort-Zurücksetzens');
        }

        setSuccess(data);
        formik.resetForm();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
  });

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <Fade in={true} timeout={500}>
            <Box>
              <CheckCircleOutlineIcon 
                sx={{ 
                  fontSize: 60, 
                  color: 'success.main',
                  mb: 2
                }} 
              />
              <Typography variant="h5" gutterBottom>
                {success.message}
              </Typography>
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  '& .MuiAlert-message': {
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              >
                {success.detail}
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                Zurück zum Login
              </Button>
            </Box>
          </Fade>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <img
            src={logo}
            alt="CyberCustos Logo"
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </Box>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          Passwort zurücksetzen
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} align="center">
          Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts, sofern diese E-Mail-Adresse existiert.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={formik.handleSubmit} noValidate>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="E-Mail-Adresse"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            margin="normal"
            autoComplete="email"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              mt: 3,
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333333',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Passwort-Zurücksetzen-Link senden'}
          </Button>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Zurück zur Anmeldung
            </Link>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ForgotPassword; 